import {
    useCallback, useEffect,
    useState, useTransition,
} from 'react'
import moment from 'moment'

import SkyNetStepperButtons from 'app/shared-components/SkyNetStepper/SkyNetStepperButtons'
import useValidation from 'app/hooks/useValidation'
import useGetLane from 'app/Apps/Pricing/LaneGroups/Lanes/hooks/useGetLane'
import strToDate from 'app/utils/date/strToDate'
import StatusHandler from 'app/shared-components/StatusHandler'

import usePreBookingInfoContext from '../PreBookingInfoContext/PreBookingInfoContext'
import {
    PreBookingEntity,
} from '../../PreBooking.types'

import PreBookingLaneDetails from './PreBookingLaneDetails'
import useGetPreBookingLaneDetailsFields from './hooks/useGetPreBookingLaneDetailsFields'

type Props = {
    disabled: boolean,
    isLoading: boolean,
    nextAction: (a: PreBookingEntity) => void | Promise<void>,
    nextLabel?: string,
    defaultValues?: Partial<PreBookingEntity>,
}

const defaultProps: Partial<Props> = {
    nextLabel: 'Create',
    defaultValues: {},
}

const PreBookingLaneDetailsContainer = ({
    disabled,
    nextAction,
    nextLabel,
    defaultValues,
    isLoading,
}: Props) => {
    const {
        activeStep,
        setActiveStep,
        value,
        setIsEdited,
    } = usePreBookingInfoContext()

    const [
        loaded,
        setLoaded,
    ] = useState<boolean>()

    const [
        isPending,
        startTransition,
    ] = useTransition()

    const {
        data: laneData,
        isDataReady,
    } = useGetLane(value.lane?.id)

    const [
        formValues,
        setFormValues,
    ] = useState<PreBookingEntity>({
        isForecasted: true,
        ...value,
        ...defaultValues,
    })
    const laneSelected = !!value.lane
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false)

    const {
        allowedProductTypes,
        fields,
        isDataReady: fieldsReady,
        isError,
        isFetching,
        error,
    } = useGetPreBookingLaneDetailsFields({
        disabled,
        laneSelected,
        tempRange: laneData?.tempRange,
        requestedHandover: formValues?.requestedHandover,
    })

    useEffect(() => {
        if (isDataReady && laneData) {
            setFormValues((prevState) => {
                return {
                    ...prevState,
                    customerExecution: laneData.customerExecution,
                    pricingAccount: laneData.pricingAccount,
                    originAirport: laneData.originAirport,
                    destinationAirport: laneData.destinationAirport,
                    originServiceCenter: laneData.originSc,
                }
            })
        }
    }, [
        laneData,
        isDataReady,
    ])

    const {
        isValid,
    } = useValidation({
        fields, values: formValues,
    })
    const backAction = useCallback(() => {
        setActiveStep(activeStep - 1)
    }, [
        setActiveStep,
        activeStep,
    ])

    const handleNextAction = useCallback(async () => {
        setSaving(true)
        await nextAction(formValues)
        setSaving(false)
    }, [
        formValues,
        nextAction,
    ])

    const handleOnChange = useCallback((params: PreBookingEntity) => {
        setFormValues((prevState) => {
            setIsEdited(true)
            const newFormValues = {
                ...params,
            }

            if (params.requestedHandover) {
                const date = strToDate(params.requestedHandover)

                newFormValues.requestedHandoverYear = date.getFullYear()
                newFormValues.requestedHandoverWeek = moment(date).week()
            } else if (prevState.requestedHandover && params.requestedHandover === null) {
                newFormValues.requestedHandoverYear = undefined
                newFormValues.requestedHandoverWeek = undefined
            }
            return {
                ...prevState,
                ...newFormValues,
            }
        })
    }, [setIsEdited])

    useEffect(() => {
        if (fieldsReady) {
            startTransition(() => {
                const requiredContainers = (formValues.requiredContainers || []).filter(({
                    productType,
                }) => {
                    return allowedProductTypes.includes(productType)
                })

                setFormValues((prevState) => {
                    return {
                        ...prevState,
                        requiredContainers: requiredContainers.length
                            ? requiredContainers : undefined,
                    }
                })
            })

            setLoaded(true)
        }
    }, [ // eslint-disable-line
        allowedProductTypes,
        fieldsReady,
        loaded,
    ])

    return (
        <div>
            <StatusHandler
                isSuccess={fieldsReady && !isPending && loaded}
                isError={isError}
                error={error}
                isFetching={isFetching || isPending}
            >
                <PreBookingLaneDetails
                    disabled={disabled}
                    onChange={handleOnChange}
                    formValues={formValues}
                    fields={fields}
                />
            </StatusHandler>
            <SkyNetStepperButtons
                isValid={isValid || saving}
                disabled={disabled}
                canGoBack
                backAction={backAction}
                nextLabel={nextLabel}
                nextAction={handleNextAction}
                isLoading={isLoading}
            />
        </div>
    )
}

PreBookingLaneDetailsContainer.defaultProps = defaultProps

export default PreBookingLaneDetailsContainer
